import noop from 'lodash/noop';
import uniq from 'lodash/uniq';
import curry from 'lodash/curry';
import find from 'lodash/find';
import take from 'lodash/take';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import prop from 'lodash/fp/prop';
import pipe from 'lodash/fp/pipe';
import map from 'lodash/fp/map';
import mapValues from 'lodash/fp/mapValues';
import orderBy from 'lodash/fp/orderBy';

const sameMid = (m, mid) =>
  Number(m && m.mid) === Number(mid);

export const mergeMatches = curry((withMatches, baseMatches) =>
  baseMatches.map(match =>
    Object.assign({}, match, find(withMatches, wm => sameMid(wm, match.mid)))
  )
);

const forecastMergeFields = [
  'teamHome',
  'teamAway',
  'scoreHome',
  'scoreAway',
  'winner',
  'assertions'
];

export const mergeMatchesScoresKeyed = curry((withMatches, baseMatches) =>
  mapValues(match =>
    Object.assign(
      {},
      match,
      pick(find(withMatches, wm => sameMid(wm, match.mid)) || {}, [
        'scoreHome',
        'scoreAway',
        'winner',
        'assertions'
      ])
    )
  )(baseMatches)
);

export const mergeMatchesKeyed = curry((withMatches, baseMatches) =>
  mapValues(match =>
    Object.assign(
      {},
      match,
      pick(find(withMatches, wm => sameMid(wm, match.mid)) || {}, forecastMergeFields)
    )
  )(baseMatches)
);

export const mergeAssertions = curry((from, to) =>
  to.map(match => ({
    ...match,
    assertions: get(
      find(from, row => sameMid(row, match.mid)),
      'assertions',
      []
    )
  }))
);

export const hasManualWinner = (match) =>
  !!match && ['home', 'away'].includes(match.winner);

export const isForecastScoreFilled = (score) =>
  typeof score === 'number' && !Number.isNaN(score);

/** true si hay exactamente un resultado cargado y el otro vacío */
export const hasPartialForecastScores = (match) => {
  if (!match) return false;
  const homeFilled = isForecastScoreFilled(match.scoreHome);
  const awayFilled = isForecastScoreFilled(match.scoreAway);
  if (!homeFilled && !awayFilled) return false;
  return homeFilled !== awayFilled;
};

export const hasCompleteForecastScores = (match) =>
  isForecastScoreFilled(match?.scoreHome) &&
  isForecastScoreFilled(match?.scoreAway);

export const hasResults = (match) => {
  if (!match) return false;
  const { scoreHome, scoreAway } = match;
  if (hasManualWinner(match)) return true;
  return scoreHome >= 0 && scoreAway >= 0;
};

export const hasBothTeams = match =>
  !isEmpty(match.teamHome) && !isEmpty(match.teamAway);

export const isTie = (match) =>
  !!match && match.scoreHome === match.scoreAway;

export const getResult = (match) => {
  if (!match) return noop();
  const {
    winner,
    scoreHome,
    scoreAway,
    teamHome,
    teamAway
  } = match;
  let home = { team: teamHome, score: scoreHome };
  let away = { team: teamAway, score: scoreAway };

  return hasManualWinner(match)
    ? {
        winner: winner === 'home' ? home : away,
        loser: winner === 'home' ? away : home
      }
    : isTie(match)
      ? noop()
      : scoreHome > scoreAway
        ? { winner: home, loser: away }
        : { winner: away, loser: home };
};

export const getTeams = (matches) =>
  (matches || []).reduce(
    (teams, match) => uniq([...teams, match.teamHome, match.teamAway]),
    []
  );

export const resolveTeam = (matchesById) => (from) => {
  const m = matchesById[from.mid];
  if (!hasResults(m)) return noop();
  return get(getResult(m), `${from.type}.team`);
};

export const initTeamsScores = teams =>
  teams.reduce((all, team) => ({ ...all, [team]: [0, 0, 0] }), {});

export const createGetPoints = teams => (team, goalDif, goalF, points) => ({
  [team]: [
    teams[team][0] + points,
    teams[team][1] + goalDif,
    teams[team][2] + goalF
  ]
});

export const createTeamPoints = ({ w, l, t }) => matches =>
  (matches || []).reduce((teams, match) => {
    if (!match) return teams;
    let getPoints = createGetPoints(teams);

    if (isTie(match)) {
      return {
        ...teams,
        ...getPoints(
          match.teamHome,
          match.scoreHome - match.scoreAway,
          match.scoreHome,
          t
        ),
        ...getPoints(
          match.teamAway,
          match.scoreAway - match.scoreHome,
          match.scoreAway,
          t
        )
      };
    }

    let { winner, loser } = getResult(match);
    return {
      ...teams,
      ...getPoints(winner.team, winner.score - loser.score, winner.score, w),
      ...getPoints(loser.team, loser.score - winner.score, loser.score, l)
    };
  }, initTeamsScores(getTeams(matches)));

export const getTeamPoints = createTeamPoints({ w: 3, l: 0, t: 1 });

export const sortByPoints = pipe(
  points =>
    Object.keys(points).map(team => ({
      team,
      points: points[team][0],
      gdif: points[team][1],
      gf: points[team][2]
    })),
  orderBy(['points', 'gdif', 'gf'], ['desc', 'desc', 'desc']),
  map(prop('team'))
);

export const allMatchesPlayed = matches =>
  !matches.some(match => !hasResults(match));

export const getGroupWinners = groups =>
  Object.keys(groups).reduce(
    (all, g) => ({
      ...all,
      [g]: allMatchesPlayed(groups[g])
        ? take(sortByPoints(getTeamPoints(groups[g])), 2)
        : []
    }),
    {}
  );
